import {
  AfterContentInit, ChangeDetectionStrategy, Component, ContentChild, ContentChildren, Directive, forwardRef,
  HostBinding, Inject,
  QueryList,
  ViewEncapsulation
} from '@angular/core';
import { CdkStep, CdkStepper } from '@angular/cdk/stepper';
import { takeUntil } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { McRAIStepLabel } from '../directives/step-label.directive';
import { AnimationCurves, AnimationDurations } from '../../core/animation/animation';
import { FocusableOption } from '@angular/cdk/a11y';

@Component({
  selector: 'mcrai-step',
  template: `<ng-template><ng-content></ng-content></ng-template>
`,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class McRAIStep extends CdkStep {
  @ContentChild(McRAIStepLabel) stepLabel: McRAIStepLabel;

  constructor(
    @Inject(forwardRef(() => McRAIStepper)) stepper: McRAIStepper
  ) {
    super(stepper);
  }
}

@Directive({
  selector: '[mcraiStepper]'
})
export class McRAIStepper extends CdkStepper implements AfterContentInit {
  @HostBinding('class.mcrai-stepper') cssClass = true;

  _stepHeader: QueryList<FocusableOption> = new QueryList<FocusableOption>();

  @ContentChildren(McRAIStep) _steps: QueryList<McRAIStep>;

  ngAfterContentInit() {
    this._steps.changes
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => this._stateChanged());
  }
}

@Component({
  selector: 'mcrai-horizontal-stepper',
  template: `<div class="mcrai-stepper__header-container">
  <ng-container *ngFor="let step of _steps; let i = index; let isLast = last">
    <mcrai-step-header
      (click)="step.select()"
      [index]="i"
      [label]="step.stepLabel || step.label"
      [active]="selectedIndex === i"
    ></mcrai-step-header>
    <div class="mcrai-stepper__header-line" *ngIf="!isLast"></div>
  </ng-container>
</div>
<div class="mcrai-stepper__container">
  <div
    class="mcrai-stepper__step"
    *ngFor="let step of _steps; let i = index"
    [class.mcrai-stepper__step--active]="selectedIndex === i"
    [@stepTransition]="_getAnimationDirection(i)"
  >
    <ng-container [ngTemplateOutlet]="step.content"></ng-container>
  </div>
</div>
`,
  styles: [`.mcrai-stepper{display:block}.mcrai-stepper__header-container{display:flex;align-items:center;margin-bottom:10px}.mcrai-stepper__header-line{flex:auto;height:1px;margin:0 5px}.mcrai-stepper__step{overflow:hidden}.mcrai-stepper__step:not(.mcrai-stepper__step--active){height:0}`],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: McRAIStepper, useExisting: McRAIHorizontalStepper }],
  animations: [
    trigger('stepTransition', [
      state('previous', style({
        transform: 'translate3d(-100%, 0, 0)',
        visibility: 'hidden'
      })),
      state('current', style({
        transform: 'none',
        visibility: 'visible'
      })),
      state('next', style({
        transform: 'translate3d(100%, 0, 0)',
        visibility: 'hidden'
      })),
      transition('* => *',
        animate(`${AnimationDurations.COMPLEX} ${AnimationCurves.DECELERATION_CURVE}`))
    ])
  ],
})
export class McRAIHorizontalStepper extends McRAIStepper {
  @HostBinding('class.mcrai-stepper--horizontal') extendCssClass = true;
}
